import React, { useCallback, useEffect, useState } from 'react';
import ConsoleShell from '../../components/admin2/ConsoleShell';
import { adminFindSupplier, adminListExclusiveSellers, adminSetExclusivesPublic, adminSetExclusiveSeller, exclusivesPublic } from '../../lib/marketExclusives';

// «حصريات معبر» (owner, 2026-10-29) — which supplier accounts the team sells
// through. Add one by its email or Maabar supplier ID, stop or resume it, and
// see each one's live products, conversations, open and paid invoices. Every
// change goes through admin_set_exclusive_seller (audited); the store page and
// the SAR / no-fee invoice follow at once.

const T = {
  ar: {
    title: 'حصريات معبر', sub: 'حسابات الموردين التي يبيع منها فريق معبر. منتجاتها النشطة تظهر في صفحة «حصريات معبر» بالمتجر، وفواتيرها بالريال بدون رسوم.',
    find: 'جوال الحساب أو إيميله أو رقم المورد (MS-…)', search: 'بحث', add: 'أضفه للحصريات', stop: 'إيقاف', resume: 'تفعيل', page: 'صفحة المتجر',
    none: 'لا يوجد بائع حصري بعد.', notFound: 'لا يوجد حساب بهذا الإيميل أو الرقم.', ambiguous: 'أكثر من حساب يطابق — اكتب الإيميل كاملًا.',
    notSupplier: 'هذا الحساب ليس حساب مورد. سجّله كمورد أولًا.', added: 'أُضيف للحصريات', saved: 'حُفظ',
    cols: { name: 'الحساب', live: 'منتجات ظاهرة', convs: 'محادثات', open: 'فواتير مفتوحة', paid: 'فواتير مدفوعة', paidSar: 'المدفوع (ر.س)', state: 'الحالة' },
    on: 'مفعّل', off: 'موقوف', note: 'ملاحظة (اختيارية)',
    pubTitle: 'ظهور القسم للعملاء', pubOn: 'ظاهر للعملاء', pubOff: 'مخفي عن العملاء', show: 'أظهره للعملاء', hide: 'أخفه عن العملاء',
    pubOffBody: 'التجار والزوار ما يشوفون البطاقة ولا الصفحة ولا المنتجات. أنت وفريق معبر وموردنا البطل تشوفونها للمراجعة.',
    pubOnBody: 'القسم ظاهر لكل التجار والزوار في الموقع والتطبيق.', confirmShow: 'تفتح «حصريات معبر» لكل العملاء الحين؟', confirmHide: 'تخفي «حصريات معبر» عن العملاء؟',
  },
  en: {
    title: 'MAABAR Exclusives', sub: 'Supplier accounts the MAABAR team sells through. Their active products show on the store\'s «MAABAR Exclusives» page; their invoices are SAR with no fee.',
    find: 'Account phone, email or supplier ID (MS-…)', search: 'Search', add: 'Add to exclusives', stop: 'Stop', resume: 'Resume', page: 'Store page',
    none: 'No exclusive seller yet.', notFound: 'No account with that email or ID.', ambiguous: 'More than one account matches — type the full email.',
    notSupplier: 'This account is not a supplier. Register it as a supplier first.', added: 'Added to exclusives', saved: 'Saved',
    cols: { name: 'Account', live: 'Live products', convs: 'Conversations', open: 'Open invoices', paid: 'Paid invoices', paidSar: 'Paid (SAR)', state: 'State' },
    on: 'Active', off: 'Stopped', note: 'Note (optional)',
    pubTitle: 'Visible to customers', pubOn: 'Visible to customers', pubOff: 'Hidden from customers', show: 'Show to customers', hide: 'Hide from customers',
    pubOffBody: 'Traders and visitors see no card, page or product. You, the MAABAR team and the exclusive seller see it to review.',
    pubOnBody: 'The section is visible to every trader and visitor, on the web and in the app.', confirmShow: 'Open «MAABAR Exclusives» to all customers now?', confirmHide: 'Hide «MAABAR Exclusives» from customers?',
  },
};
T.zh = T.en;
const fmt = (n) => Number(n || 0).toLocaleString('en-US');

export default function ConsoleExclusives({ user, profile, lang }) {
  const isAr = lang === 'ar';
  const c = T[lang] || T.ar;
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  const [note, setNote] = useState('');
  const [found, setFound] = useState(null);   // a profile, { ambiguous }, or 'none'
  const [toast, setToast] = useState('');
  const [pub, setPub] = useState(null);
  useEffect(() => { exclusivesPublic().then(setPub); }, []);
  const togglePub = async () => {
    if (!window.confirm(pub ? c.confirmHide : c.confirmShow)) return;
    const r = await adminSetExclusivesPublic(!pub);
    if (!r.ok) { flash(r.error); return; }
    setPub(!pub); flash(!pub ? c.pubOn : c.pubOff);
  };
  const flash = (m) => { setToast(m); setTimeout(() => setToast(''), 2400); };

  const load = useCallback(async () => {
    const r = await adminListExclusiveSellers();
    if (!r.ok) setErr(r.error);
    setRows(r.rows);
  }, []);
  useEffect(() => { load(); }, [load]);

  const search = async (e) => {
    e.preventDefault();
    setFound(null);
    const p = await adminFindSupplier(q);
    setFound(p || 'none');
  };
  const set = async (id, active, msg) => {
    const r = await adminSetExclusiveSeller(id, active, note);
    if (!r.ok) { flash(r.error); return; }
    flash(msg); setFound(null); setQ(''); setNote(''); load();
  };

  const name = (r) => r.company_name || r.full_name || r.email || String(r.supplier_id || r.id).slice(0, 8);
  return (
    <ConsoleShell user={user} profile={profile} lang={lang} active="market-exclusives">
      <div className="ac-page" style={{ fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
        <div className="ac-page-head">
          <div>
            <h1 className="ac-h1">{c.title}</h1>
            <p className="ac-sub">{c.sub}</p>
          </div>
          <a className="ac-btn ac-btn-sm" href="/market/exclusives" target="_blank" rel="noreferrer">{c.page}</a>
        </div>

        <div className="ac-card" data-excl-public={pub === null ? '' : pub ? 'on' : 'off'} style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12, borderColor: pub === false ? '#B4372C' : undefined }}>
          <div style={{ flex: '1 1 260px' }}>
            <p className="ac-card-title" style={{ margin: 0 }}>{c.pubTitle}: <span style={{ color: pub ? '#2F5D3A' : '#B4372C' }}>{pub === null ? '…' : pub ? c.pubOn : c.pubOff}</span></p>
            <p style={{ margin: '4px 0 0', fontSize: 12.5, color: '#666' }}>{pub ? c.pubOnBody : c.pubOffBody}</p>
          </div>
          {pub !== null && <button type="button" className="ac-btn ac-btn-sm" data-excl-public-toggle="1" onClick={togglePub}>{pub ? c.hide : c.show}</button>}
        </div>

        <form className="ac-card" onSubmit={search} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={c.find} dir="ltr" data-excl-find="1" style={{ flex: '1 1 260px', minWidth: 0, height: 34 }} />
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder={c.note} maxLength={500} style={{ flex: '1 1 200px', minWidth: 0, height: 34 }} />
          <button type="submit" className="ac-btn ac-btn-sm" disabled={!q.trim()}>{c.search}</button>
          {found && (
            <div style={{ flexBasis: '100%', fontSize: 13 }} data-excl-found={found === 'none' ? 'none' : found.ambiguous ? 'ambiguous' : found.id}>
              {found === 'none' ? c.notFound : found.ambiguous ? c.ambiguous : (
                <span style={{ display: 'inline-flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <b>{name(found)}</b><span dir="ltr" style={{ color: '#666' }}>{[found.phone, found.email, found.maabar_supplier_id].filter(Boolean).join(' · ')}</span>
                  {String(found.role || '').toLowerCase() !== 'supplier' ? <span style={{ color: '#B4372C' }}>{c.notSupplier}</span>
                    : <button type="button" className="ac-btn ac-btn-sm" data-excl-add="1" onClick={() => set(found.id, true, c.added)}>{c.add}</button>}
                </span>
              )}
            </div>
          )}
        </form>

        {err && <div className="ac-card" style={{ color: '#B4372C', marginBottom: 12 }}>{err}</div>}
        {rows === null ? <div className="ac-skel" style={{ height: 120 }} /> : rows.length === 0 ? <div className="ac-placeholder">{c.none}</div> : (
          <div className="ac-card" style={{ overflowX: 'auto' }}>
            <table data-excl-rows={rows.length} style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'start', color: '#666' }}>
                  {['name', 'live', 'convs', 'open', 'paid', 'paidSar', 'state'].map((k) => <th key={k} style={{ padding: '8px 6px', textAlign: 'start', fontWeight: 600 }}>{c.cols[k]}</th>)}
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.supplier_id} data-excl-seller={r.supplier_id} style={{ borderTop: '1px solid #eee' }}>
                    <td style={{ padding: '8px 6px' }}><b>{name(r)}</b><div dir="ltr" style={{ color: '#888', fontSize: 11.5 }}>{r.email} {r.maabar_supplier_id || ''}</div>{r.note && <div style={{ color: '#888', fontSize: 11.5 }}>{r.note}</div>}</td>
                    <td style={{ padding: '8px 6px' }}>{fmt(r.live_products)}</td>
                    <td style={{ padding: '8px 6px' }}>{fmt(r.conversations)}</td>
                    <td style={{ padding: '8px 6px' }}>{fmt(r.invoices_open)}</td>
                    <td style={{ padding: '8px 6px' }}>{fmt(r.invoices_paid)}</td>
                    <td style={{ padding: '8px 6px' }}>{Number(r.paid_sar || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td style={{ padding: '8px 6px' }}><span data-excl-state={r.active ? 'on' : 'off'} style={{ color: r.active ? '#2F5D3A' : '#B4372C', fontWeight: 600 }}>{r.active ? c.on : c.off}</span></td>
                    <td style={{ padding: '8px 6px' }}><button type="button" className="ac-btn ac-btn-sm" data-excl-toggle={r.supplier_id} onClick={() => set(r.supplier_id, !r.active, c.saved)}>{r.active ? c.stop : c.resume}</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {toast && <div className="ac-toast" data-toast="1">{toast}</div>}
    </ConsoleShell>
  );
}
